import useSWR from 'swr';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { URLParameters } from '@/app/common/urlParameters';
import { fetchWithTracingAndLogging } from '@/app/components/hotel-detail/common/fetchWithTracing';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useInitialParams } from '../InitialParamsProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useFetchHotelStatic } from './useFetchHotelStatic';
import getSearchParams from '../../search/common/getSearchParams';
import { isValidHotelId } from '../common/common.helper.ts';

export function useRecommendedHotels() {
  const { internalHotelId, pageType } = useInitialParams();
  const { searchInfoData, localization, commonData } = usePageWrapperData();
  const urlSearchParams = useUrlSearchParams();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const searchParams = getSearchParams();
  const origin = searchParams[URLParameters.ORIGIN];
  const destination = searchParams[URLParameters.DESTINATION];
  const ages = searchParams[URLParameters.AGES];
  const limInv = searchParams[URLParameters.LIMITED_INVENTORY];
  const { accommodationOnly } = urlSearchParams;
  const { currency, lang } = localization;

  const originParam = accommodationOnly ? '' : `&origin=${origin}`;
  const loggingService = accommodationOnly ? 'memsearch-ho' : 'memsearch-dp';
  const hotelCityIdParam = hotelStaticData?.cityId ? `&hotelCity=${hotelStaticData?.cityId}` : '';
  const agesParam = ages ? `&ages=${ages}` : '';
  const limitedInventoryParam = limInv === '1' ? `&${URLParameters.LIMITED_INVENTORY}=1` : '';

  // set the limit to results + 1 to avoid double calls when the current hotel is in the returned list.
  // A hotel-detail deep link carries no destination, so interpolating it unconditionally used to send
  // the literal "destination=undefined" - truthy on the API side. Gated the way originParam already is.
  const destinationParam = destination ? `&destination=${destination}` : '';

  const url = `${BASE_PATH}/api/hotel-detail/recommended-hotels?pageType=${pageType}&internalHotelId=${internalHotelId}${originParam}${destinationParam}${hotelCityIdParam}&datefrom=${urlSearchParams.checkin}&dateto=${urlSearchParams.checkout}&adults=${urlSearchParams.adults}${agesParam}&category=${hotelStaticData?.rating}&crn=${currency}&lang=${lang}&bp=${localization.businessProfile}${limitedInventoryParam}`;

  const key: string | undefined =
    !isValidHotelId(internalHotelId) ||
    urlSearchParams.totalRooms > 1 ||
    !hotelStaticData?.name ||
    (!accommodationOnly && !origin) ||
    urlSearchParams.adults >= 6
      ? undefined
      : url;

  return useSWR(
    key,
    async () =>
      fetchWithTracingAndLogging<MemSearchProductData[]>(
        url,
        {
          headers: {
            'content-type': 'application/json',
          },
        },
        getLoggingInfo(pageType, loggingService, 'recommendedHotels'),
        searchInfoData.searchTraceParent,
        commonData
      ),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 1000 * 60 * 30,
    }
  );
}
